// useInactivityLogout — kicks the user back to /login after a stretch of
// no mouse / keyboard / touch / scroll activity.
//
// Why this exists: the backend pool only has ~20 connections and is
// shared across many tenants on the VPS.  An operator who walks away
// from the management portal with the tab open is a resource leak waiting
// to happen — every poll / interaction holds a lease on something.
// Auto-logging them out at the 10-minute mark closes the management
// session cleanly: localStorage is cleared, the user is bounced to
// /login, and any subsequent API call will fail the global auth gate.
//
// Only arms when there's a cached user (i.e. someone is signed in).
// Listens to mousemove, mousedown, keydown, touchstart, scroll.  We
// debounce via a single timer that resets on each event — no per-event
// React state updates, so the hook is cheap.

import { useEffect, useRef } from "react";

const DEFAULT_IDLE_MS = 10 * 60 * 1000;          // 10 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

export function useInactivityLogout({
  enabled = true,
  idleMs = DEFAULT_IDLE_MS,
  onLogout,
} = {}) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const arm = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Final guard: don't fire if the user has already been logged
        // out by another tab / manual click.
        const u = window.localStorage.getItem("user");
        if (!u) return;
        try {
          window.localStorage.setItem("session_expired", "idle");
          window.localStorage.removeItem("user");
        } catch (_) {}
        if (typeof onLogout === "function") onLogout();
      }, idleMs);
    };

    arm();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, arm, { passive: true }));

    // Cross-tab sync — if another tab logs out / in we re-evaluate.
    const onStorage = (e) => {
      if (e.key === "user") arm();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, arm));
      window.removeEventListener("storage", onStorage);
    };
  }, [enabled, idleMs, onLogout]);
}
